import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { merge, startWith } from 'rxjs';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxComponent } from '../../../../../osp-ui/components/checkbox/checkbox.component';
import { ErrorComponent } from '../../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../../osp-ui/components/form/form-field/form-field.component';
import { InputNumberComponent } from '../../../../../osp-ui/components/form/input-number/input-number.component';
import { InputTextComponent } from '../../../../../osp-ui/components/form/input-text/input-text.component';
import { LocationSearchInputComponent } from '../../../../../shared/components/maps/components/location-search-field/location-search-field.component';
import { MapSearchPlaceResult } from '../../../../../shared/components/maps/contract/map-search-place-result.contract';
import { MapDefaultOptionsDirective } from '../../../../../shared/components/maps/directives/map-default-options.directive';

const LOCATION_FORM_CONTROL_NAMES = ['street', 'streetNumber', 'postalCode', 'city', 'lat', 'lon'] as const;

@Component({
  selector: 'osp-location-address-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GoogleMap,
    MapMarker,
    MapDefaultOptionsDirective,
    LocationSearchInputComponent,
    FormFieldComponent,
    LabelComponent,
    InputTextComponent,
    InputNumberComponent,
    ErrorComponent,
    CheckboxComponent,
  ],
  templateUrl: './location-address-form.component.html',
  styleUrls: ['./location-address-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationAddressFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly validationStateVersion = signal(0);

  @Input({ required: true }) formGroup!: FormGroup;
  @Input({ required: true }) mapCenter!: google.maps.LatLngLiteral;
  @Input() mapMarkerPosition: google.maps.LatLngLiteral | null = null;
  @Input() isMapLoaded = false;
  @Input() showAutomaticGeofence = false;

  @Output() readonly mapClick = new EventEmitter<google.maps.MapMouseEvent>();
  @Output() readonly locationChanges = new EventEmitter<MapSearchPlaceResult>();

  ngOnInit(): void {
    merge(this.formGroup.statusChanges, this.formGroup.valueChanges, this.formGroup.events)
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshValidationState());
  }

  protected get locationFormErrors(): ValidationErrors | null {
    this.validationStateVersion();
    return this.isLocationFormInteracted() ? this.formGroup.errors : null;
  }

  private refreshValidationState(): void {
    this.validationStateVersion.update((version) => version + 1);
    this.cdr.markForCheck();
  }

  private isLocationFormInteracted(): boolean {
    return LOCATION_FORM_CONTROL_NAMES.some((name) => {
      const control = this.formGroup.get(name);
      return control?.dirty || control?.touched;
    });
  }
}
